<template>
  <b-modal header-class="ion-bg-modal-header" size="xl"
           centered scrollable no-close-on-backdrop
           v-model="visible">

    <template #modal-header>
      <div class="fw-bold">{{ $t('chargeBox.cbTitle') }} {{ $t('common.create') }}</div>
    </template>

    <div class="row">
      <div class="col-xl-12">

        <!-- Charge Box Info Start -->
        <div class="ion-modal-form-group-title">{{ $t('chargeBox.cbInfo') }}</div>
        <div class="row mb-3">
          <div class="col-xl-6">
            <label class="form-label" for="cb-create-name">
              <span>{{ $t('chargeBox.chargeBoxName') }}</span>
              <i class="uil uil-asterisk text-danger ms-1"></i>
            </label>
            <b-form-input class="form-control"
                          type="text"
                          id="cb-create-name"
                          v-model="form.chargeBoxName"
                          :class="{ 'is-invalid': submitted && $v.form.chargeBoxName.$error }" />
            <div v-if="submitted && $v.form.chargeBoxName.$error" class="invalid-feedback">
              <span v-if="!$v.form.chargeBoxName.required">{{ $t('validation.cbNameIsRequired') }}</span>
            </div>
          </div>
          <div class="col-xl-6">
            <label class="form-label" for="cb-create-id">
              <span>{{ $t('chargeBox.chargeBoxId') }}</span>
              <i class="uil uil-asterisk text-danger ms-1"></i>
            </label>
            <b-form-input class="form-control"
                          type="text"
                          id="cb-create-id"
                          v-model="form.chargeBoxId"
                          :class="{ 'is-invalid': submitted && $v.form.chargeBoxId.$error }" />
            <div v-if="submitted && $v.form.chargeBoxId.$error" class="invalid-feedback">
              <span v-if="!$v.form.chargeBoxId.required">{{ $t('validation.cbIdIsRequired') }}</span>
            </div>
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-xl-6">
            <label class="form-label" for="cb-create-charger-type">
              <span>{{ $t('chargeBox.chargerType') }}</span>
              <i class="uil uil-asterisk text-danger ms-1"></i>
            </label>
            <div class="btn-group w-100" role="group">
              <input type="radio" class="btn-check" id="typeBtn1" autocomplete="off"
                     v-model="form.chargerType" value="SLOW" />
              <label class="btn btn-outline-secondary" for="typeBtn1">{{ $t('common.slow') }}</label>
              <input type="radio" class="btn-check" id="typeBtn2" autocomplete="off"
                     v-model="form.chargerType" value="FAST" />
              <label class="btn btn-outline-secondary" for="typeBtn2">{{ $t('common.fast') }}</label>
            </div>
          </div>
          <div class="col-xl-6">
            <label class="form-label" for="cb-create-max-kw">
              <span>{{ $t('common.chargeSpeed') }}</span>
              <i class="uil uil-asterisk text-danger ms-1"></i>
            </label>
            <div class="btn-group w-100" role="group">
              <template v-if="form.chargerType === 'SLOW'">
                <div class="btn-group" role="group" style="width: 33%">
                  <input type="radio" class="btn-check" id="maxKwBtn1" autocomplete="off"
                         v-model="form.maxKw" value="10" />
                  <label class="btn btn-outline-secondary" for="maxKwBtn1">{{ '10 kW' }}</label>
                </div>
              </template>
              <template v-else-if="form.chargerType === 'FAST'">
                <input type="radio" class="btn-check" id="maxKwBtn1" autocomplete="off"
                       v-model="form.maxKw" value="90" />
                <label class="btn btn-outline-secondary" for="maxKwBtn1">{{ '90 kW' }}</label>
                <input type="radio" class="btn-check" id="maxKwBtn2" autocomplete="off"
                       v-model="form.maxKw" value="120" />
                <label class="btn btn-outline-secondary" for="maxKwBtn2">{{ '120 kW' }}</label>
                <input type="radio" class="btn-check" id="maxKwBtn3" autocomplete="off"
                       v-model="form.maxKw" value="180" />
                <label class="btn btn-outline-secondary" for="maxKwBtn3">{{ '180 kW' }}</label>
              </template>
            </div>
          </div>
        </div>

        <div class="row mb-4">
          <div class="col-xl-6">
            <label class="form-label" for="cb-create-pwd">
              <span>{{ $t('chargeBox.ocppPassword') }}</span>
              <i class="uil uil-asterisk text-danger ms-1"></i>
            </label>
            <b-form-input class="form-control"
                          type="text"
                          id="cb-create-pwd"
                          v-model="form.password"
                          :placeholder="defaultPw"
                          :class="{ 'is-invalid': submitted && $v.form.password.$error }" />
            <div v-if="submitted && $v.form.password.$error" class="invalid-feedback">
              <span v-if="!$v.form.password.required">{{ $t('validation.pwdIsRequired') }}</span>
            </div>
          </div>
          <div class="col-xl-6"></div>
        </div>
        <!-- Charge Box Info End -->

        <!-- Charge Connector Info Start -->
        <div class="ion-modal-form-group-title">{{ $t('chargeBox.connector') }}</div>
        <div class="row mb-4">

          <div class="col-xl-6">
            <label class="form-label" for="ct-update-ct-type">
              <span>{{ $t('chargeBox.connectorType') }} 1</span>
            </label>
            <multiselect id="ct-update-ct-type"
                         v-model="form.connectors[0].connectorType"
                         label="text" track-by="value"
                         :options="connectorTypeOptions"
                         :placeholder="$t('alert.selectConnectorType')"
                         selectLabel=""
                         selectedLabel=""
                         :deselectLabel="$t('common.unselect')"
                         :searchable="false" :allow-empty="true" />
          </div>
          <div class="col-xl-6">
            <label class="form-label" for="ct-update-ct-type">
              <span>{{ $t('chargeBox.connectorType') }} 2</span>
            </label>
            <multiselect id="ct-update-ct-type"
                         v-model="form.connectors[1].connectorType"
                         label="text" track-by="value"
                         :options="connectorTypeOptions"
                         :placeholder="$t('alert.selectConnectorType')"
                         selectLabel=""
                         selectedLabel=""
                         :deselectLabel="$t('common.unselect')"
                         :searchable="false" :allow-empty="true" />
          </div>
        </div>
        <!-- Charge Connector Info End -->

        <!-- Headquarters & Station Info Start -->
        <div class="ion-modal-form-group-title">{{ $_app.user.roles[0] === 'ADMIN' ? $t('chargeBox.hqAndCpInfo') : $t('chargeBox.cpInfo') }}</div>
        <div class="row mb-3" v-if="$_app.user.roles[0] === 'ADMIN'">
          <div class="col-xl-6">
            <label class="form-label" for="cb-create-hq">{{ $t('chargeBox.hqName') }}</label>
            <multiselect id="cb-create-hq"
                         v-model="hqId"
                         label="text" track-by="value"
                         :options="hqOptions"
                         openDirection="top"
                         :placeholder="$t('alert.selectHq')"
                         selectLabel=""
                         selectedLabel=""
                         :deselectLabel="$t('common.unselect')"
                         :searchable="false" :allow-empty="true"
                         :disabled="$_app.user.roles[0] === 'HQ'"
                         @select="selectHq"
                         @remove="removeHq" />
          </div>
          <div class="col-xl-6">
            <label class="form-label" for="cb-create-hq-manager">{{ $t('chargeBox.hqManagerName') }}</label>
            <b-form-input class="form-control" type="text" id="cb-create-hq-manager" v-model="hqManagerName" disabled />
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-xl-6">
            <label class="form-label" for="cb-create-charge-place">{{ $t('common.cp') }}</label>
            <multiselect id="cb-create-cp"
                         v-model="cpId"
                         label="text" track-by="value"
                         :options="cpOptions"
                         openDirection="top"
                         :placeholder="$t('alert.selectCp')"
                         selectLabel=""
                         selectedLabel=""
                         :deselectLabel="$t('common.unselect')"
                         :searchable="false" :allow-empty="true"
                         @select="selectCp"
                         @remove="removeCp" />
          </div>
          <div class="col-xl-6">
            <label class="form-label" for="cb-create-cp-manager">{{ $t('chargeBox.cpManagerName') }}</label>
            <b-form-input class="form-control" type="text" id="cb-create-cp-manager" v-model="cpManagerName" disabled />
          </div>
        </div>
        <!-- Headquarters & Station Info End -->

      </div>
    </div>

    <template #modal-footer>
      <div class="w-100 text-end">
        <b-button variant="secondary" class="w-xs p-1 me-3" @click="close">{{ $t('btn.close') }}</b-button>
        <b-button variant="success" class="w-xs p-1" @click="save">{{ $t('btn.save') }}</b-button>
      </div>
    </template>

  </b-modal>
</template>

<script>
import { http } from '@/auth-api';
import i18n from '@/i18n';
import Swal from 'sweetalert2';
import Multiselect from 'vue-multiselect';
import { required } from 'vuelidate/lib/validators';

export default {
  components: { Multiselect },
  data() {
    return {
      visible: false,
      submitted: false,
      hqOptions: [],
      cpOptions: [],
      connectorTypeOptions: [],
      form: {
        chargeBoxName: null,
        chargeBoxId: null,
        chargerType: 'SLOW',
        maxKw: '10',
        password: null,
        connectors: [
          { connectorId: 1, connectorType: null },
          { connectorId: 2, connectorType: null },
        ]
      },
      hqId: null,
      hqManagerName: null,
      cpId: null,
      cpManagerName: null,
      defaultPw: 'spocha12!@'
    }
  },
  validations: {
    form: {
      chargeBoxName: { required },
      chargeBoxId: { required },
      password: { required }
    }
  },
  watch: {
    'form.chargerType': {
      handler(newVal) {
        const vm = this;
        vm.form.maxKw = newVal === 'SLOW' ? '10' : '90';
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    show() {
      const vm = this;

      vm.getHqOptions();
      vm.getConnectorTypeOptions();
      vm.visible = true;
      vm.form.password = vm.defaultPw;
    },
    getHqOptions() {
      const vm = this;

      const _url = '/onmapi/view/Hq/listAll';

      http.get(_url, {}).then(res => {
        vm.hqOptions = [];

        res.data.items.forEach(item => {
          vm.hqOptions.push({
            text: item.hqName,
            value: item.id,
            manager: item.hqRepresentAccount ? item.hqRepresentAccount.name : '-'
          });
        });
      }).then(() => {
        if (this.$_app.user.roles[0] === 'HQ') {
          vm.hqId = vm.hqOptions.find(ele => ele.value === this.$_app.user.hqId);
          vm.selectHq();
        }
      }).catch(err => {
        console.error('Headquarters List Data Error :: ', err)
      });
    },
    selectHq() {
      const vm = this;

      if (!this.$_utils.isEmpty(vm.hqId)) {
        vm.hqManagerName = vm.hqId.manager;
      } else {
        vm.hqManagerName = null;
      }

      vm.getCpOptions();
    },
    getConnectorTypeOptions() {
      const vm = this;
      vm.connectorTypeOptions = Object.entries(this.$_app.code.connectorType).map(([key, value]) => ({ text: key, value: value }));
    },
    removeHq() {
      const vm = this;
      vm.hqManagerName = null;

      vm.cpId = null;
      vm.cpManagerName = null;
      vm.cpOptions = [];
    },
    selectCp() {
      const vm = this;

      if (!this.$_utils.isEmpty(vm.cpId)) {
        vm.cpManagerName = vm.cpId.manager;
      } else {
        vm.cpManagerName = null;
      }
    },
    removeCp() {
      const vm = this;
      vm.cpManagerName = null;
    },
    getCpOptions() {
      const vm = this;
      const _url = '/onmapi/view/ChargePlace/list';

      vm.cpOptions = [];
      vm.cpId = null;
      vm.cpManagerName = null;

      if (!this.$_utils.isEmpty(vm.hqId)) {
        http.post(_url, {
          page: 1,
          limit: 1000,
          searchConditions: [{ 'searchKey': 'hqId', 'searchValue': vm.hqId.value }],
          sortCondition: { sortDirection: 'DESC' }
        }).then(res => {
          res.data.items.forEach(item => {
            vm.cpOptions.push({
              text: item.placeName,
              value: item.placeId,
              manager: item.placeRepresentAccount ? item.placeRepresentAccount.name : '-'
            });
          });
        }).catch(err => {
          console.error('Charge Place List Error :: ', err)
        });
      }
    },
    close() {
      const vm = this;

      this.$emit('callback');
      vm.submitted = false;
      vm.resetForm();
      vm.visible = false;
    },
    resetForm() {
      const vm = this;

      vm.form = {
        chargeBoxName: null,
        chargeBoxId: null,
        chargerType: 'SLOW',
        maxKw: '10',
        password: null,
        connectors: [
          { connectorId: 1, connectorType: null },
          { connectorId: 2, connectorType: null }
        ]
      }
      vm.hqOptions = [];
      vm.cpOptions = [];
      vm.connectorTypeOptions = [];

      vm.hqId = null;
      vm.hqManagerName = null;
      vm.cpId = null;
      vm.cpManagerName = null;

      vm.$nextTick(() => {
        this.$v.$reset();
      });
    },
    save() {
      const vm = this;
      vm.submitted = true;

      if (this.$_utils.isEmpty(vm.form.password)) {
        vm.form.password = vm.defaultPw
      }

      this.$v.$touch();

      if (!this.$v.$invalid) {
        Swal.fire({
          title: i18n.t('alert.confirmCreate'),
          showCancelButton: true,
          confirmButtonText: i18n.t('btn.ok'),
          cancelButtonText: i18n.t('btn.cancel')
        }).then((result) => {
          if (result.isConfirmed) {
            vm.saveItems();
          }
        });
      }
    },
    saveItems() {
      const vm = this;
      const _url = '/onmapi/view/ChargePoint/create';

      let _form = Object.assign({}, vm.form);

      if (!this.$_utils.isEmpty(vm.cpId)) {
        _form.chargePlace = {};
        _form.chargePlace.placeId = vm.cpId.value;
      }

      _form.connectors = vm.form.connectors
        .filter(
          ele => ele.connectorType != null && ele.connectorType !== undefined
        ).map(
          ele => ({ connectorId: ele.connectorId, connectorType: ele.connectorType.value })
        );

      http.post(_url, _form).then(res => {
        if (res) {
          Swal.fire({
            icon: 'info',
            title: i18n.t('alert.created'),
            confirmButtonText: i18n.t('btn.close')
          });
          vm.close();
        }
      }).catch(err => {
        console.error('Charge Box Create Error :: ', err)

        Swal.fire({
          icon: 'error',
          title: i18n.t('alert.error'),
          confirmButtonText: i18n.t('btn.close')
        });
      });
    },
  }
}
</script>

<style src='vue-multiselect/dist/vue-multiselect.min.css'></style>
